/* File: useCalendarReminderPoller.js
 * Path: ananunadm/src/helpers/useCalendarReminderPoller.js
 * Description: Polls the calendar-event API once per minute and injects an
 *   in-app notification for every alarm whose fire-time has passed but has not
 *   yet been marked as notified by the backend scheduler (alarm.notifiedAt is
 *   absent).  Each unique alarm is shown at most once per browser session
 *   (tracked via a useRef Set).  Notifications are added through the existing
 *   SocketContext so they appear in the standard notification panel and can be
 *   dismissed with the X button just like any other notification.
 *
 *   Fire-time formula (mirrors backend calendarAlarm.service.js):
 *     fireTime = event.start + parseDuration(alarm.trigger)
 *   e.g.  trigger="-PT15M"  →  15 minutes before event start
 *         trigger="PT0H"    →  exactly at start time
 *         trigger="-P1D"    →  one day before
 */
'use strict';

import { useEffect, useRef } from 'react';
import * as API from './api';
import config from '@/config';
import { useSocket } from './socket';
import { useUser } from './userContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 60_000;   // check every 60 s
const LOOKBACK_HOURS   = 24;       // look back 24 h for alarms missed after restart

// ---------------------------------------------------------------------------
// ISO 8601 duration → milliseconds  (mirrors calendarAlarm.service.js)
// Supports: PnW, PnD, PTnH, PTnM, PTnS — leading '-' means "before" → negative
// ---------------------------------------------------------------------------
function parseDurationToMs(dur) {
	if (!dur) return 0;
	const sign     = dur.startsWith('-') ? -1 : 1;
	const abs      = dur.replace(/^[+-]?P/, '');
	const tIdx     = abs.indexOf('T');
	const datePart = tIdx >= 0 ? abs.slice(0, tIdx) : abs;
	const timePart = tIdx >= 0 ? abs.slice(tIdx + 1) : '';

	let ms = 0;
	const w = datePart.match(/(\d+)W/);
	const d = datePart.match(/(\d+)D/);
	const h = timePart.match(/(\d+)H/);
	const m = timePart.match(/(\d+)M/);
	const s = timePart.match(/(\d+)S/);

	if (w) ms += parseInt(w[1]) * 7 * 86_400_000;
	if (d) ms += parseInt(d[1])     * 86_400_000;
	if (h) ms += parseInt(h[1])      * 3_600_000;
	if (m) ms += parseInt(m[1])         * 60_000;
	if (s) ms += parseInt(s[1])          * 1_000;

	return sign * ms;
}

// ---------------------------------------------------------------------------
// Generate a 24-char hex string that passes MongoDB ObjectId validation.
// This lets the standard removeNotification socket path (findByIdAndRemove)
// receive a castable id and still emit 'notificationRemoved' so the X button
// removes the item from the panel.
// ---------------------------------------------------------------------------
function generateObjectId() {
	const ts   = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
	const rand = Array.from({ length: 16 }, () =>
		Math.floor(Math.random() * 16).toString(16)
	).join('');
	return ts + rand;
}

// ---------------------------------------------------------------------------
// Hook — mount once inside the authenticated app shell (e.g. main.jsx)
// ---------------------------------------------------------------------------
export function useCalendarReminderPoller() {
	const { setNewNotification } = useSocket();
	const { user }               = useUser();

	// Track alarm keys already shown this session so we don't repeat them
	const shownRef = useRef(new Set());

	useEffect(() => {
		const eventRoute = config.api.calendar?.event;
		if (!user?._id || !eventRoute) return;

		async function checkAlarms() {
			const now         = new Date();
			const windowStart = new Date(now.getTime() - LOOKBACK_HOURS * 3_600_000);

			try {
				const res = await API.get({
					route: eventRoute,
					query: {
						// Events that have at least one alarm subdoc
						'alarms.0': { $exists: true },
						// Only consider events that haven't ended more than 24 h ago
						end: { $gte: windowStart.toISOString() },
					},
				});

				const events = Array.isArray(res) ? res : (res?.items ?? []);

				for (const event of events) {
					if (!Array.isArray(event.alarms) || event.alarms.length === 0) continue;

					const eventStart = new Date(event.start);

					for (const alarm of event.alarms) {
						// Backend already stamped this alarm → nothing to do
						if (alarm.notifiedAt) continue;

						// Stable key for this alarm within this session
						const alarmKey = `${event._id}-${alarm._id || alarm.trigger}`;
						if (shownRef.current.has(alarmKey)) continue;

						// Calculate the moment this alarm should fire
						const fireTime = new Date(
							eventStart.getTime() + parseDurationToMs(alarm.trigger || '-PT15M')
						);

						// Not due yet
						if (fireTime > now) continue;

						// ── Alarm is due and unseen ─────────────────────────────────────
						shownRef.current.add(alarmKey);

						const title = event.title || 'Event';
						const desc  = alarm.description || title;
						const id    = generateObjectId();

						// Build multilingual notification matching the backend format
						const multilang = (text) => ({
							en: text, ru: text, hy: text,
							tr: text, ar: text, zh: text,
							fa: text, he: text, fr: text,
							es: text, pt: text,
						});

						setNewNotification({
							_id:        id,
							createdAt:  new Date().toISOString(),
							date:       new Date().toISOString(),
							importance: 'medium',
							route:      'calendar',
							read:       [],
							name:       multilang(`Reminder: ${title}`),
							desc:       multilang(desc),
						});

						console.log(`[CalendarReminderPoller] Notified: "${title}" | trigger: ${alarm.trigger}`);
					}
				}
			} catch (err) {
				console.warn('[CalendarReminderPoller]', err?.message || err);
			}
		}

		// Run immediately on mount, then on every interval tick
		checkAlarms();
		const intervalId = setInterval(checkAlarms, POLL_INTERVAL_MS);

		return () => clearInterval(intervalId);
	}, [user?._id, setNewNotification]);
}
